import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  GET_COMERCIO,
  GENERAR_LICENCIA,
  RENOVAR_LICENCIA,
  SUSPENDER_COMERCIO,
  REACTIVAR_COMERCIO,
  GET_MODULOS,
  ASIGNAR_MODULOS_LICENCIA
} from '../graphql/queries'
import './ComercioDetalle.css'

function ComercioDetalle() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [showGenerarModal, setShowGenerarModal] = useState(false)
  const [showRenovarModal, setShowRenovarModal] = useState(false)
  const [showModulosModal, setShowModulosModal] = useState(false)
  const [licenciaData, setLicenciaData] = useState({
    tipo_licencia: 'renta_mensual',
    fecha_vencimiento: ''
  })
  const [codigoGenerado, setCodigoGenerado] = useState(null)
  const [modulosSeleccionados, setModulosSeleccionados] = useState([])

  const { data, loading, refetch } = useQuery(GET_COMERCIO, {
    variables: { id }
  })

  const [generarLicencia, { loading: generando }] = useMutation(GENERAR_LICENCIA, {
    onCompleted: (data) => {
      setCodigoGenerado(data.generarLicencia.codigo)
      refetch()
    }
  })

  const [renovarLicencia, { loading: renovando }] = useMutation(RENOVAR_LICENCIA, {
    onCompleted: (data) => {
      setCodigoGenerado(data.renovarLicencia.codigo)
      refetch()
    }
  })

  const [suspenderComercio] = useMutation(SUSPENDER_COMERCIO, {
    onCompleted: () => refetch()
  })

  const [reactivarComercio] = useMutation(REACTIVAR_COMERCIO, {
    onCompleted: () => refetch()
  })

  const comercio = data?.comercio

  // Query para obtener modulos disponibles filtrados por tipo de interfaz del comercio
  const { data: modulosData } = useQuery(GET_MODULOS, {
    variables: { soloActivos: true, tipoInterfaz: comercio?.tipo_interfaz },
    skip: !comercio?.tipo_interfaz
  })

  const [asignarModulos, { loading: asignandoModulos }] = useMutation(ASIGNAR_MODULOS_LICENCIA, {
    onCompleted: () => {
      setShowModulosModal(false)
      refetch()
    }
  })
  const todosLosModulos = modulosData?.modulos || []

  // Efecto para inicializar modulos seleccionados cuando se abre el modal
  useEffect(() => {
    if (showModulosModal && comercio?.licencia_activa?.modulos_codigos && todosLosModulos.length > 0) {
      // Solo incluir modulos premium (no core)
      const modulosPremium = comercio.licencia_activa.modulos_codigos.filter(codigo => {
        const modulo = todosLosModulos.find(m => m.codigo === codigo)
        return modulo && !modulo.es_core
      })
      setModulosSeleccionados(modulosPremium)
    } else if (showModulosModal && !comercio?.licencia_activa?.modulos_codigos) {
      // Si no hay modulos asignados, iniciar con array vacio
      setModulosSeleccionados([])
    }
  }, [showModulosModal]) // Solo depender de showModulosModal para evitar loop infinito

  if (loading) {
    return (
      <div className="loading" style={{ height: '50vh' }}>
        <div className="spinner"></div>
      </div>
    )
  }

  if (!comercio) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <p>Comercio no encontrado</p>
          <button className="btn btn-primary" onClick={() => navigate('/comercios')}>
            Volver
          </button>
        </div>
      </div>
    )
  }

  const handleGenerarLicencia = () => {
    generarLicencia({
      variables: {
        input: {
          comercio_id: parseInt(id),
          tipo_licencia: licenciaData.tipo_licencia,
          fecha_vencimiento: licenciaData.fecha_vencimiento || null
        }
      }
    })
  }

  const handleRenovarLicencia = () => {
    if (!comercio.licencia_activa) return

    renovarLicencia({
      variables: {
        licencia_id: comercio.licencia_activa.id,
        tipo_licencia: licenciaData.tipo_licencia,
        fecha_vencimiento: licenciaData.fecha_vencimiento || null
      }
    })
  }

  const handleSuspender = () => {
    if (confirm('¿Está seguro de suspender este comercio?')) {
      suspenderComercio({
        variables: { id, motivo: 'Suspendido desde el gestor' }
      })
    }
  }

  const handleReactivar = () => {
    reactivarComercio({ variables: { id } })
  }

  const handleToggleModulo = (codigoModulo) => {
    setModulosSeleccionados(prev => {
      if (prev.includes(codigoModulo)) {
        return prev.filter(c => c !== codigoModulo)
      } else {
        return [...prev, codigoModulo]
      }
    })
  }

  const handleGuardarModulos = () => {
    if (!comercio?.licencia_activa?.id) return

    asignarModulos({
      variables: {
        licenciaId: comercio.licencia_activa.id,
        modulosCodigos: modulosSeleccionados
      }
    })
  }

  // Nombres legibles para categorias
  const nombreCategoria = (cat) => {
    const nombres = {
      core: 'Core',
      modulos_adicionales: 'Módulos Adicionales',
      hotel: 'Hotel',
      restaurante: 'Restaurante',
      retail: 'Retail'
    }
    return nombres[cat] || cat
  }

  // Organizar modulos por categoria
  const modulosPorCategoria = () => {
    const categorias = {}
    todosLosModulos.forEach(modulo => {
      if (!categorias[modulo.categoria]) {
        categorias[modulo.categoria] = []
      }
      categorias[modulo.categoria].push(modulo)
    })
    return categorias
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    alert('Código copiado al portapapeles')
  }

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'activo': return <span className="badge badge-success">Activo</span>
      case 'pendiente': return <span className="badge badge-warning">Pendiente</span>
      case 'suspendido': return <span className="badge badge-danger">Suspendido</span>
      default: return <span className="badge badge-gray">{estado}</span>
    }
  }

  return (
    <div className="comercio-detalle">
      {/* Header */}
      <div className="page-header flex-between">
        <div>
          <button className="btn btn-secondary btn-sm mb-1" onClick={() => navigate('/comercios')}>
            ← Volver
          </button>
          <h1 className="page-title">{comercio.nombre_comercial}</h1>
          <p className="page-subtitle">NIT: {comercio.nit}</p>
        </div>
        <div className="flex gap-1">
          {comercio.estado === 'suspendido' ? (
            <button className="btn btn-success" onClick={handleReactivar}>
              Reactivar
            </button>
          ) : (
            <button className="btn btn-danger" onClick={handleSuspender}>
              Suspender
            </button>
          )}
          {comercio.licencia_activa ? (
            <button className="btn btn-primary" onClick={() => setShowRenovarModal(true)}>
              Renovar Licencia
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => setShowGenerarModal(true)}>
              Generar Licencia
            </button>
          )}
        </div>
      </div>

      <div className="detalle-grid">
        {/* Información del comercio */}
        <div className="card">
          <div className="card-header">
            <h3>Información del Comercio</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Razón Social</span>
                <span className="info-value">{comercio.razon_social}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Estado</span>
                <span className="info-value">{getEstadoBadge(comercio.estado)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Tipo de Interfaz</span>
                <span className="info-value" style={{ textTransform: 'capitalize' }}>
                  {comercio.tipo_interfaz}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{comercio.email || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Teléfono</span>
                <span className="info-value">{comercio.telefono || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Ciudad</span>
                <span className="info-value">{comercio.ciudad || '-'}</span>
              </div>
              <div className="info-item full-width">
                <span className="info-label">Dirección</span>
                <span className="info-value">{comercio.direccion || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Licencia activa */}
        <div className="card">
          <div className="card-header flex-between">
            <h3>Licencia Activa</h3>
            {comercio.licencia_activa && (
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setShowModulosModal(true)}
              >
                Gestionar Modulos
              </button>
            )}
          </div>
          <div className="card-body">
            {comercio.licencia_activa ? (
              <div className="licencia-activa">
                <div className="licencia-estado">
                  {comercio.licencia_activa.dias_restantes > 0 ? (
                    <span className="dias-restantes positivo">
                      {comercio.licencia_activa.dias_restantes} dias restantes
                    </span>
                  ) : (
                    <span className="dias-restantes negativo">
                      Vencida hace {Math.abs(comercio.licencia_activa.dias_restantes)} dias
                    </span>
                  )}
                </div>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Tipo</span>
                    <span className="info-value">{comercio.licencia_activa.tipo_licencia_nombre}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Vencimiento</span>
                    <span className="info-value">{comercio.licencia_activa.fecha_vencimiento}</span>
                  </div>
                  <div className="info-item full-width">
                    <span className="info-label">Codigo</span>
                    <div className="codigo-box">
                      <code>{comercio.licencia_activa.codigo_licencia}</code>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => copyToClipboard(comercio.licencia_activa.codigo_licencia)}
                      >
                        Copiar
                      </button>
                    </div>
                  </div>
                </div>

                {/* Modulos activos */}
                {comercio.licencia_activa.modulos && comercio.licencia_activa.modulos.length > 0 && (
                  <div className="modulos-section">
                    <span className="info-label">Modulos Activos</span>
                    <div className="modulos-tags">
                      {comercio.licencia_activa.modulos.map(modulo => (
                        <span
                          key={modulo.codigo}
                          className={`modulo-tag ${modulo.es_core ? 'core' : 'premium'}`}
                          title={modulo.descripcion || modulo.nombre}
                        >
                          {modulo.nombre}
                          {modulo.es_core && <small> (CORE)</small>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-muted">
                <p>Este comercio no tiene una licencia activa</p>
                <button className="btn btn-primary" onClick={() => setShowGenerarModal(true)}>
                  Generar Licencia
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Historial */}
      <div className="card mt-3">
        <div className="card-header">
          <h3>Historial de Acciones</h3>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {comercio.historial && comercio.historial.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Acción</th>
                  <th>Descripción</th>
                  <th>Usuario</th>
                </tr>
              </thead>
              <tbody>
                {comercio.historial.map((item) => (
                  <tr key={item.id}>
                    <td>{new Date(item.created_at).toLocaleString('es-CO')}</td>
                    <td style={{ textTransform: 'capitalize' }}>{item.accion.replace('_', ' ')}</td>
                    <td>{item.descripcion}</td>
                    <td>{item.usuario?.nombre || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center text-muted" style={{ padding: '2rem' }}>
              No hay historial registrado
            </p>
          )}
        </div>
      </div>

      {/* Modal Generar Licencia */}
      {showGenerarModal && (
        <div className="modal-overlay" onClick={() => !codigoGenerado && setShowGenerarModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {codigoGenerado ? 'Licencia Generada' : 'Generar Nueva Licencia'}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowGenerarModal(false)
                  setCodigoGenerado(null)
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {codigoGenerado ? (
                <div className="codigo-generado">
                  <p>La licencia se ha generado correctamente:</p>
                  <div className="codigo-box-grande">
                    <code>{codigoGenerado}</code>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => copyToClipboard(codigoGenerado)}
                  >
                    Copiar Código
                  </button>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Tipo de Licencia</label>
                    <select
                      className="form-select"
                      value={licenciaData.tipo_licencia}
                      onChange={(e) => setLicenciaData({ ...licenciaData, tipo_licencia: e.target.value })}
                    >
                      <option value="compra">Compra (Permanente)</option>
                      <option value="renta_mensual">Renta Mensual (30 días)</option>
                      <option value="renta_trimestral">Renta Trimestral (90 días)</option>
                      <option value="renta_anual">Renta Anual (365 días)</option>
                      <option value="prueba">Prueba (7 días)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fecha de Vencimiento (opcional)</label>
                    <input
                      type="date"
                      className="form-input"
                      value={licenciaData.fecha_vencimiento}
                      onChange={(e) => setLicenciaData({ ...licenciaData, fecha_vencimiento: e.target.value })}
                    />
                    <small className="text-muted">
                      Si no se especifica, se calculará automáticamente según el tipo
                    </small>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {!codigoGenerado && (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowGenerarModal(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleGenerarLicencia}
                    disabled={generando}
                  >
                    {generando ? 'Generando...' : 'Generar'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Renovar Licencia */}
      {showRenovarModal && (
        <div className="modal-overlay" onClick={() => !codigoGenerado && setShowRenovarModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {codigoGenerado ? 'Licencia Renovada' : 'Renovar Licencia'}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowRenovarModal(false)
                  setCodigoGenerado(null)
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {codigoGenerado ? (
                <div className="codigo-generado">
                  <p>La licencia se ha renovado correctamente:</p>
                  <div className="codigo-box-grande">
                    <code>{codigoGenerado}</code>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => copyToClipboard(codigoGenerado)}
                  >
                    Copiar Código
                  </button>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Tipo de Licencia</label>
                    <select
                      className="form-select"
                      value={licenciaData.tipo_licencia}
                      onChange={(e) => setLicenciaData({ ...licenciaData, tipo_licencia: e.target.value })}
                    >
                      <option value="compra">Compra (Permanente)</option>
                      <option value="renta_mensual">Renta Mensual (30 días)</option>
                      <option value="renta_trimestral">Renta Trimestral (90 días)</option>
                      <option value="renta_anual">Renta Anual (365 días)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fecha de Vencimiento (opcional)</label>
                    <input
                      type="date"
                      className="form-input"
                      value={licenciaData.fecha_vencimiento}
                      onChange={(e) => setLicenciaData({ ...licenciaData, fecha_vencimiento: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {!codigoGenerado && (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowRenovarModal(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleRenovarLicencia}
                    disabled={renovando}
                  >
                    {renovando ? 'Renovando...' : 'Renovar'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestionar Modulos */}
      {showModulosModal && (
        <div className="modal-overlay" onClick={() => setShowModulosModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Gestionar Modulos — <span style={{ textTransform: 'capitalize' }}>{comercio.tipo_interfaz}</span></h3>
              <button
                className="modal-close"
                onClick={() => setShowModulosModal(false)}
              >
                x
              </button>
            </div>
            <div className="modal-body">
              <p className="text-muted mb-2">
                Modulos disponibles para interfaz <strong style={{ textTransform: 'capitalize' }}>{comercio.tipo_interfaz}</strong>.
                Los modulos CORE estan incluidos automaticamente.
              </p>

              {Object.entries(modulosPorCategoria()).map(([categoria, modulos]) => (
                <div key={categoria} className="modulos-categoria">
                  <h4 className="categoria-titulo">{nombreCategoria(categoria)}</h4>
                  <div className="modulos-lista">
                    {modulos.map(modulo => (
                      <label
                        key={modulo.codigo}
                        className={`modulo-checkbox ${modulo.es_core ? 'disabled' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={modulo.es_core || modulosSeleccionados.includes(modulo.codigo)}
                          disabled={modulo.es_core}
                          onChange={() => handleToggleModulo(modulo.codigo)}
                        />
                        <span className="modulo-info">
                          <span className="modulo-nombre">
                            {modulo.nombre}
                            {modulo.es_core && <span className="badge badge-sm badge-gray ml-1">CORE</span>}
                            {!modulo.es_core && <span className="badge badge-sm badge-warning ml-1">PREMIUM</span>}
                          </span>
                          {modulo.descripcion && (
                            <span className="modulo-descripcion">{modulo.descripcion}</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <div className="modulos-resumen">
                <strong>Modulos seleccionados:</strong>{' '}
                {modulosSeleccionados.length > 0
                  ? modulosSeleccionados.join(', ')
                  : 'Solo modulos CORE'}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModulosModal(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleGuardarModulos}
                disabled={asignandoModulos}
              >
                {asignandoModulos ? 'Guardando...' : 'Guardar Modulos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ComercioDetalle
